import React, { useEffect, useState } from 'react';
import { usePortalStore } from '../../store';
import { CheckCircle, ChevronRight, X, RefreshCw } from 'lucide-react';
import type { NotificationCard } from '../../types';
import { BurnRateGauge } from '../BurnRateGauge';

const NEWSFEED_CATEGORIES = [
  { value: '', label: 'All' },
  { value: 'competitor', label: 'Competitor' },
  { value: 'market', label: 'Market' },
  { value: 'regulatory', label: 'Regulatory' },
  { value: 'technology', label: 'Technology' },
];

const CARD_STYLES: Record<NotificationCard['type'], { border: string; badge: string; icon: string }> = {
  blocker:   { border: 'border-red-700/60 bg-red-950/30',    badge: 'bg-red-900 text-red-200',    icon: '🔴' },
  challenge: { border: 'border-yellow-700/60 bg-yellow-950/20', badge: 'bg-yellow-900 text-yellow-200', icon: '🟡' },
  signal:    { border: 'border-emerald-700/60 bg-emerald-950/20', badge: 'bg-emerald-900 text-emerald-200', icon: '🟢' },
};

const MatchReportBlock: React.FC = () => {
  const { matchReport, triggerBriefingRegeneration, loadMatchReport } = usePortalStore();
  const [regenerating, setRegenerating] = useState(false);

  const handleRegenerate = async () => {
    setRegenerating(true);
    await triggerBriefingRegeneration();
    await loadMatchReport();
    setRegenerating(false);
  };

  if (!matchReport) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 animate-pulse">
        <div className="h-4 bg-slate-700 rounded w-3/4 mb-3" />
        <div className="h-4 bg-slate-700 rounded w-full mb-3" />
        <div className="h-4 bg-slate-700 rounded w-2/3" />
      </div>
    );
  }

  const sentences = [...matchReport.sentences];
  while (sentences.length < 3) sentences.push('');

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Co-founder Match Report</h2>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500">
            {matchReport.generatedAt ? new Date(matchReport.generatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
          </span>
          <button
            onClick={handleRegenerate}
            disabled={regenerating}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-xs text-slate-300 font-medium transition-colors"
            title="Regenerate briefing from live backlog state"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${regenerating ? 'animate-spin' : ''}`} />
            {regenerating ? 'Regenerating...' : 'Regenerate'}
          </button>
        </div>
      </div>
      <div className="space-y-3">
        {sentences.map((sentence, i) => sentence && (
          <p key={i} className={`text-slate-${i === 0 ? '100' : i === 1 ? '200' : '300'} leading-relaxed`}>
            {sentence}
          </p>
        ))}
      </div>
      <div className="mt-4 pt-4 border-t border-slate-700 flex gap-6 text-xs text-slate-500">
        <span>{matchReport.epicsCompleted} epics completed</span>
        <span>{matchReport.epicsInProgress} in progress</span>
      </div>
    </div>
  );
};

const CollectBlock: React.FC = () => {
  const { completedEpics, acknowledgeCompletedEpic } = usePortalStore();

  if (completedEpics.length === 0) return null;

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
      <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">
        Ready to Collect — {completedEpics.length} epic{completedEpics.length !== 1 ? 's' : ''} done
      </h2>
      <div className="space-y-3">
        {completedEpics.map(epic => (
          <div key={epic.id} className="flex items-start justify-between gap-4 bg-slate-900/60 rounded-lg p-4 border border-emerald-800/30">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                <span className="font-semibold text-slate-100 text-sm truncate">{epic.title}</span>
              </div>
              {epic.summary && (
                <p className="text-xs text-slate-400 ml-6 line-clamp-2">{epic.summary}</p>
              )}
            </div>
            <button
              onClick={() => acknowledgeCompletedEpic(epic.id)}
              className="shrink-0 px-3 py-1.5 bg-emerald-800 hover:bg-emerald-700 text-emerald-100 text-xs font-semibold rounded transition-colors"
            >
              Collect
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const NewsfeedBlock: React.FC = () => {
  const { newsfeed, newsfeedCategory, loadNewsfeed, setNewsfeedCategory } = usePortalStore();

  const handleCategoryChange = (cat: string) => {
    setNewsfeedCategory(cat);
    loadNewsfeed(cat);
  };

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Intelligence Newsfeed</h2>
        <div className="flex flex-wrap gap-1">
          {NEWSFEED_CATEGORIES.map(c => (
            <button
              key={c.value}
              onClick={() => handleCategoryChange(c.value)}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                newsfeedCategory === c.value
                  ? 'bg-cyan-800 text-cyan-100'
                  : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>
      {newsfeed.length === 0 ? (
        <p className="text-slate-500 text-sm italic">No signals yet — Watchtower runs overnight.</p>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
          {newsfeed.map(item => (
            <div key={item.id} className="bg-slate-900/50 rounded p-4 border border-slate-700/50">
              <div className="flex items-start justify-between mb-1">
                <span className="text-sm font-semibold text-slate-200">{item.title}</span>
                <span className="text-xs text-slate-500 ml-4 shrink-0">{item.source}</span>
              </div>
              <p className="text-xs text-slate-400 mb-1">{item.body}</p>
              <span className="text-xs text-slate-600">{item.category}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const NotificationSidebar: React.FC = () => {
  const { notifications, dismissNotification, navigateTo } = usePortalStore();

  const handleAction = (target: string | null) => {
    if (target) navigateTo(target);
  };

  return (
    <div className="w-full lg:w-80 shrink-0 space-y-3">
      <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider px-1">
        Notifications {notifications.length > 0 && (
          <span className="ml-2 px-1.5 py-0.5 bg-slate-700 text-slate-300 text-xs rounded-full">
            {notifications.length}
          </span>
        )}
      </h2>
      {notifications.length === 0 ? (
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 text-sm text-slate-500 italic">
          Queue is clear — nothing needs you right now.
        </div>
      ) : (
        notifications.map(card => {
          const style = CARD_STYLES[card.type] ?? CARD_STYLES.signal;
          return (
            <div key={card.id} className={`border rounded-lg p-4 ${style.border}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span>{style.icon}</span>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${style.badge}`}>
                    {card.type.toUpperCase()}
                  </span>
                </div>
                <button
                  onClick={() => dismissNotification(card.id)}
                  className="text-slate-500 hover:text-slate-300 transition-colors"
                  aria-label="Dismiss"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className="text-sm font-semibold text-slate-100 mb-1">{card.title}</p>
              <p className="text-xs text-slate-400 mb-3">{card.body}</p>
              <div className="flex flex-wrap gap-2">
                {card.actions.map((action, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      if (action.target) handleAction(action.target);
                      else dismissNotification(card.id);
                    }}
                    className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded font-medium transition-colors ${
                      i === 0
                        ? 'bg-slate-600 hover:bg-slate-500 text-slate-100'
                        : 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                    }`}
                  >
                    {action.label}
                    {action.target && <ChevronRight className="w-3 h-3" />}
                  </button>
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export const CommandCentreView: React.FC = () => {
  const { loadMatchReport, loadNotifications, loadCompletedEpics, loadNewsfeed } = usePortalStore();

  useEffect(() => {
    loadMatchReport();
    loadNotifications();
    loadCompletedEpics();
    loadNewsfeed();
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-100 mb-1">Command Centre</h1>
        <p className="text-slate-500 text-sm">
          {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 items-start">
        {/* Main column */}
        <div className="flex-1 min-w-0 space-y-6">
          <MatchReportBlock />
          <BurnRateGauge />
          <CollectBlock />
          <NewsfeedBlock />
        </div>

        {/* Notification sidebar */}
        <NotificationSidebar />
      </div>
    </div>
  );
};
